if (!customElements.get('koko-navigation')) {
  class KokoNavigation extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.panel = this.querySelector('.koko-nav-panel');
      this.bar = this.querySelector('.koko-nav-bar');
      this.wrapper = this.closest('.header-section');
      this.mobileToggle = this.querySelector('[data-menu-toggle]');
      this.shopTrigger = this.querySelector('[data-shop-trigger]');
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.desktop = matchMedia('(min-width: 1024px)');
      this.hover = matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.previousY = Math.max(0, scrollY);
      this.directionDistance = 0;
      this.inertState = new Map();
      const on = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: this.events.signal });

      for (const trigger of [this.mobileToggle, this.shopTrigger]) {
        on(trigger, 'click', event => {
          event.preventDefault();
          this.lastTrigger = trigger;
          const keepHoverOpen = this.desktop.matches && this.openedOnHover;
          this.openedOnHover = false;
          this.setMenu(keepHoverOpen || !this.isOpen, event.detail === 0);
        });
        on(trigger, 'keydown', event => {
          if (event.key !== 'ArrowDown') return;
          event.preventDefault();
          this.lastTrigger = trigger;
          this.openedOnHover = false;
          this.setMenu(true, true);
        });
      }
      on(this.shopTrigger, 'pointerenter', () => {
        if (this.hover.matches && !this.isOpen) {
          this.openedOnHover = true;
          this.setMenu(true);
        }
      });
      on(this, 'pointerenter', () => clearTimeout(this.closeTimer));
      on(this, 'pointerleave', () => {
        if (!this.hover.matches || this.contains(document.activeElement)) return;
        this.closeTimer = setTimeout(() => this.setMenu(false), 180);
      });
      on(this, 'focusout', () => {
        setTimeout(() => {
          if (this.desktop.matches && !this.contains(document.activeElement) && !this.matches(':hover')) this.setMenu(false);
        }, 0);
      });
      on(this, 'focusin', () => this.classList.remove('is-hidden'));
      for (const link of this.querySelectorAll('[data-nav-dismiss]')) {
        on(link, 'click', () => this.setMenu(false), { capture: true });
        on(link, 'pointerenter', () => { if (this.hover.matches) this.setMenu(false); });
        on(link, 'focus', () => { if (this.desktop.matches) this.setMenu(false); });
      }
      on(document, 'pointerdown', event => {
        if (this.isOpen && !this.contains(event.target)) this.setMenu(false);
      });
      on(document, 'keydown', event => this.onKeydown(event));
      on(document, 'cart-drawer:open', () => this.setMenu(false));
      on(this.querySelector('[data-menu-close]'), 'click', () => {
        this.setMenu(false);
        (this.desktop.matches ? this.shopTrigger : this.mobileToggle).focus({ preventScroll: true });
      });
      for (const tab of this.tabs) {
        on(tab, 'click', () => this.selectCategory(tab));
        on(tab, 'keydown', event => {
          const index = this.tabs.indexOf(tab);
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.selectCategory(this.tabs[next]);
          this.tabs[next].focus({ preventScroll: true });
        });
      }
      for (const category of this.querySelectorAll('[data-category-panel]')) {
        const rail = category.querySelector('.koko-nav-products');
        on(rail, 'scroll', () => this.updateRail(category), { passive: true });
        for (const direction of ['prev', 'next']) {
          on(category.querySelector(`[data-carousel-${direction}]`), 'click', () => {
            rail.scrollBy({ left: rail.clientWidth * (direction === 'next' ? 1 : -1), behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
          });
        }
      }
      on(window, 'scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.updateScroll();
        });
      }, { passive: true });
      on(window, 'resize', () => this.measure());
      on(this.desktop, 'change', () => {
        this.setMenu(false);
        this.measure();
      });
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.bar);
      const announcement = document.querySelector('.announcement-bar-section');
      if (announcement) this.resizeObserver.observe(announcement);
      this.measure();
    }

    disconnectedCallback() {
      this.setMenu(false);
      this.events?.abort();
      this.events = null;
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.closeTimer);
    }

    measure() {
      this.origin = this.wrapper.getBoundingClientRect().top + scrollY;
      if (document.body.classList.contains('template-index')) {
        this.dataset.home = String(!!document.querySelector('#main-content > .section-rosayaa-banner:first-child'));
      }
      document.documentElement.style.setProperty('--header-height', `${this.bar.offsetHeight}px`);
      this.updateScroll(true);
      this.updateRails();
    }

    updateScroll(resize = false) {
      const y = Math.max(0, scrollY);
      const delta = y - this.previousY;
      this.classList.toggle('is-sticky', y > 8);
      const top = y <= 8 ? Math.max(0, this.origin - y) : 0;
      this.style.setProperty('--koko-nav-top', `${top}px`);
      document.documentElement.style.setProperty('--header-offset', `${top}px`);
      if (y <= 8 || this.isOpen || this.contains(document.activeElement)) {
        this.classList.remove('is-hidden');
        this.directionDistance = 0;
      } else if (!resize && delta !== 0) {
        this.directionDistance = Math.sign(delta) === Math.sign(this.directionDistance) ? this.directionDistance + delta : delta;
        if (Math.abs(this.directionDistance) > 6) {
          this.classList.toggle('is-hidden', this.directionDistance > 0);
          this.directionDistance = 0;
        }
      }
      this.previousY = y;
    }

    setMenu(open, keyboard = false) {
      clearTimeout(this.closeTimer);
      if (!this.panel) return;
      if (!open) this.openedOnHover = false;
      this.isOpen = open;
      this.panel.hidden = !open;
      this.classList.toggle('is-open', open);
      for (const trigger of [this.mobileToggle, this.shopTrigger]) trigger.setAttribute('aria-expanded', String(open));
      this.mobileToggle.setAttribute('aria-label', open ? this.mobileToggle.dataset.closeLabel : this.mobileToggle.dataset.openLabel);
      this.mobileToggle.setAttribute('title', this.mobileToggle.getAttribute('aria-label'));
      const modal = open && !this.desktop.matches;
      document.body.classList.toggle('koko-nav-scroll-locked', modal);
      if (modal) {
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-label', this.bar.getAttribute('aria-label'));
        for (const element of document.querySelectorAll('#main-content, .footer-section')) {
          if (!this.inertState.has(element)) this.inertState.set(element, element.inert);
          element.inert = true;
        }
      } else {
        this.removeAttribute('role');
        this.removeAttribute('aria-modal');
        this.removeAttribute('aria-label');
        for (const [element, value] of this.inertState) element.inert = value;
        this.inertState.clear();
      }
      if (open) {
        this.classList.remove('is-hidden');
        requestAnimationFrame(() => {
          this.updateRails();
          if (keyboard) this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true')?.focus({ preventScroll: true });
        });
      }
    }

    selectCategory(tab) {
      for (const item of this.tabs) {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
      }
      for (const panel of this.querySelectorAll('[data-category-panel]')) panel.hidden = panel.dataset.categoryPanel !== tab.dataset.category;
      this.panel.scrollTop = 0;
      const tabList = tab.parentElement;
      const tabEnd = tab.offsetLeft + tab.offsetWidth;
      if (tab.offsetLeft < tabList.scrollLeft) tabList.scrollLeft = tab.offsetLeft;
      else if (tabEnd > tabList.scrollLeft + tabList.clientWidth) tabList.scrollLeft = tabEnd - tabList.clientWidth;
      this.updateRails();
    }

    updateRails() {
      for (const category of this.querySelectorAll('[data-category-panel]')) this.updateRail(category);
    }

    updateRail(category) {
      if (category.hidden || !this.isOpen) return;
      const rail = category.querySelector('.koko-nav-products');
      const controls = category.querySelector('.koko-nav-carousel-controls');
      const edgeTolerance = parseFloat(getComputedStyle(rail).paddingInlineStart) + 2;
      controls.hidden = !this.desktop.matches || rail.scrollWidth <= rail.clientWidth + 2;
      category.querySelector('[data-carousel-prev]').disabled = rail.scrollLeft <= edgeTolerance;
      category.querySelector('[data-carousel-next]').disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.setMenu(false);
        (this.desktop.matches ? this.shopTrigger : this.mobileToggle).focus({ preventScroll: true });
      } else if (event.key === 'Tab' && !this.desktop.matches) {
        const elements = [...this.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(element => element.tabIndex >= 0 && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (event.shiftKey && (document.activeElement === first || !this.contains(document.activeElement))) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    }
  }
  customElements.define('koko-navigation', KokoNavigation);
}
